import {
    BaseScoreResult,
    QuestionnaireResponse,
    Item,
    findItemByLinkId,
    getAnswerCode
} from './types';

const SCORE_EXTENSION_URL = "http://hl7.org/fhir/StructureDefinition/questionnaire-optionScore";

export type IgiSeverity =
    | 'ausente'
    | 'subclinica'
    | 'moderada'
    | 'grave';

export interface IgiScoreResult extends BaseScoreResult {
    totalScore: number;
    severity: IgiSeverity;
    details: Record<string, number>;
}

function getItemScoreFromOption(item: Item | undefined): number {
    if (!item) return 0;
    const code = getAnswerCode(item);
    if (code === undefined) return 0;

    const selectedOption = item.answerOption?.find(opt => opt.valueCoding?.code === code);
    const scoreExtension = selectedOption?.extension?.find(ext => ext.url === SCORE_EXTENSION_URL);
    const score = scoreExtension?.valueInteger ?? parseInt(code, 10);
    return Number.isFinite(score) ? Math.min(4, Math.max(0, score)) : 0;
}

export function getIgiSeverity(totalScore: number): IgiSeverity {
    if (totalScore <= 7) return 'ausente';
    if (totalScore <= 14) return 'subclinica';
    if (totalScore <= 21) return 'moderada';
    return 'grave';
}

const IGI_ITEM_LINK_IDS = [
    'difficulty-falling-asleep',
    'difficulty-staying-asleep',
    'early-waking',
    'sleep-satisfaction',
    'sleep-interference',
    'others-perception',
    'sleep-anxiety'
] as const;

export function processIgiQuestionnaire(response: QuestionnaireResponse): IgiScoreResult {
    const rootItems = response.item || [];

    const details: Record<string, number> = {};
    let totalScore = 0;

    IGI_ITEM_LINK_IDS.forEach(linkId => {
        const item = findItemByLinkId(rootItems, linkId);
        const score = getItemScoreFromOption(item);
        details[linkId] = score;
        totalScore += score;
    });

    const severity = getIgiSeverity(totalScore);

    return {
        questionnaireId: response.questionnaire?.split('/')?.[1],
        questionnaireResponseId: response.id,
        totalScore,
        severity,
        details
    };
}
